import React, { useState, useEffect } from 'react';
import realTimeValidationService from '../../services/realTimeValidationService';

interface ColumnMappingConfig {
  field: string;
  possibleNames: string[];
  required: boolean;
  defaultValue?: unknown;
  type: 'string' | 'number' | 'percentage' | 'currency' | 'date';
  description?: string;
  format?: string;
  validValues?: string[];
  minValue?: number;
  maxValue?: number;
  pattern?: string;        // Regex pattern for validation
  errorMessage?: string;   // Custom error message for validation
  metadata?: any;          // Additional metadata from backend
}

interface ColumnMappingInterfaceProps {
  csvHeaders: string[];
  fieldsToMap: string[];
  columnMappings: Record<string, string>;
  fieldConfigs?: Record<string, ColumnMappingConfig>;
  onChange: (field: string, csvHeader: string) => void;
  previewData?: Record<string, any>[];
  validationErrors?: Record<string, string[]>; // Field-specific validation errors from backend
  isLoading?: boolean;                        // Loading state for backend operations
  onValidateMapping?: () => void;              // Function to trigger validation
  serverSuggestedMappings?: Record<string, string>; // Mappings suggested by the server
}

const ColumnMappingInterface: React.FC<ColumnMappingInterfaceProps> = ({
  csvHeaders,
  fieldsToMap,
  columnMappings,
  fieldConfigs = {},
  onChange,
  previewData = [],
  validationErrors = {},
  isLoading = false,
  onValidateMapping,
  serverSuggestedMappings = {}
}) => {
  const [suggestedMappings, setSuggestedMappings] = useState<Record<string, string[]>>({});
  const [showSuggestionTooltip, setShowSuggestionTooltip] = useState<string | null>(null);
  const [autoMappingApplied, setAutoMappingApplied] = useState(false);

  // Use the enhanced validation service to generate suggestions for each field
  useEffect(() => {
    if (csvHeaders.length > 0 && fieldsToMap.length > 0) {
      // Use enhanced service for mapping suggestions
      const automaticSuggestions = realTimeValidationService.suggestColumnMappings(
        csvHeaders,
        fieldConfigs
      );

      // Convert the single suggestions to arrays for the dropdown
      const suggestionArrays: Record<string, string[]> = {};

      // For each field, create an array of suggestions starting with the automatic one
      fieldsToMap.forEach(field => {
        const automaticSuggestion = automaticSuggestions[field];

        // Get possible names from the field config
        const possibleNames = fieldConfigs[field]?.possibleNames || [field.replace(/_/g, ' ')];
        const cleanedPossibleNames = possibleNames.map(name => name.trim().toLowerCase());

        // Find matching headers based on inclusion
        const matchingSuggestions = csvHeaders.filter(header => {
          const cleanedHeader = header.trim().toLowerCase();
          return cleanedPossibleNames.some(possibleName =>
            cleanedHeader.includes(possibleName) || possibleName.includes(cleanedHeader)
          );
        });

        // Sort suggestions by match quality (put automatic suggestion first if it exists)
        if (automaticSuggestion) {
          const filteredSuggestions = matchingSuggestions.filter(
            header => header !== automaticSuggestion
          );
          suggestionArrays[field] = [automaticSuggestion, ...filteredSuggestions];
        } else {
          // Sort by match quality
          const sortedSuggestions = [...matchingSuggestions].sort((a, b) => {
            const aLower = a.trim().toLowerCase();
            const bLower = b.trim().toLowerCase();

            // Exact matches first
            const aExactMatches = cleanedPossibleNames.filter(name => name === aLower).length;
            const bExactMatches = cleanedPossibleNames.filter(name => name === bLower).length;

            if (aExactMatches > bExactMatches) return -1;
            if (aExactMatches < bExactMatches) return 1;

            // Then partial matches
            const aMatches = cleanedPossibleNames.filter(name => aLower.includes(name)).length;
            const bMatches = cleanedPossibleNames.filter(name => bLower.includes(name)).length;

            return bMatches - aMatches;
          });

          suggestionArrays[field] = sortedSuggestions;
        }
      });

      setSuggestedMappings(suggestionArrays);
    }
  }, [csvHeaders, fieldsToMap, fieldConfigs]);

  // Apply suggested mappings automatically for high-confidence matches
  useEffect(() => {
    if (autoMappingApplied || !csvHeaders.length || !fieldsToMap.length) {
      return;
    }

    const newMappings: Record<string, string> = { ...columnMappings };
    let mappingsChanged = false;

    // Only apply suggestions for unmapped fields
    fieldsToMap.forEach(field => {
      if (!columnMappings[field] && suggestedMappings[field]?.length > 0) {
        // If we have a perfect match (first suggestion is good enough), apply it
        const bestMatch = suggestedMappings[field][0];
        const possibleNames = fieldConfigs[field]?.possibleNames || [field.replace(/_/g, ' ')];
        const cleanedPossibleNames = possibleNames.map(name => name.trim().toLowerCase());

        // Check if it's an exact match with any possible name
        if (cleanedPossibleNames.includes(bestMatch.trim().toLowerCase())) {
          newMappings[field] = bestMatch;
          mappingsChanged = true;
        }
      }
    });

    if (mappingsChanged) {
      // Apply all automatic mappings at once to avoid multiple re-renders
      Object.entries(newMappings).forEach(([field, header]) => {
        if (header && !columnMappings[field]) {
          onChange(field, header);
        }
      });
    }

    setAutoMappingApplied(true);
  }, [suggestedMappings, columnMappings, fieldsToMap, onChange, fieldConfigs, autoMappingApplied, csvHeaders]);

  // Function to apply automatic mapping to all fields
  const applyAutoMapping = () => {
    // Use server suggestions if available, otherwise client-side
    const hasServerSuggestions = Object.keys(serverSuggestedMappings).length > 0;
    const automaticSuggestions = hasServerSuggestions
      ? serverSuggestedMappings
      : realTimeValidationService.suggestColumnMappings(csvHeaders, fieldConfigs);

    // Apply all suggestions at once
    Object.entries(automaticSuggestions).forEach(([field, header]) => {
      if (header && csvHeaders.includes(header)) {
        onChange(field, header);
      }
    });

    // After applying auto-mapping, trigger validation if provided
    if (onValidateMapping) {
      setTimeout(() => onValidateMapping(), 100);
    }
  };

  // Preview a data sample for the selected column
  const getSampleValues = (headerName: string): string[] => {
    if (!previewData || !previewData.length || !headerName) {
      return [];
    }

    const values = previewData
      .slice(0, 3)
      .map(row => String(row[headerName] || ''))
      .filter(value => value.trim() !== '');

    return values.length > 0 ? values : ['No sample data available'];
  };

  // Identify conflict indicators where the same CSV header is mapped to multiple fields
  const getMappingConflicts = (): Record<string, string[]> => {
    const headerToFields: Record<string, string[]> = {};

    // Group field names by the header they're mapped to
    Object.entries(columnMappings).forEach(([field, header]) => {
      if (!header) return;

      if (!headerToFields[header]) {
        headerToFields[header] = [];
      }

      headerToFields[header].push(field);
    });

    // Keep only headers mapped to multiple fields
    return Object.fromEntries(
      Object.entries(headerToFields).filter(([_, fields]) => fields.length > 1)
    );
  };

  const mappingConflicts = getMappingConflicts();

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Column Mapping</h2>
        <div className="flex gap-2">
          {onValidateMapping && (
            <button
              onClick={onValidateMapping}
              disabled={isLoading}
              className={`px-3 py-1.5 text-sm rounded transition-colors flex items-center ${
                isLoading ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {isLoading && <span className="mr-1 inline-block h-3 w-3 rounded-full border-2 border-t-green-600 animate-spin"></span>}
              Validate Mapping
            </button>
          )}
          <button
            onClick={applyAutoMapping}
            disabled={isLoading}
            className={`px-3 py-1.5 text-sm rounded transition-colors flex items-center ${
              isLoading ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Auto-Map All Fields
          </button>
        </div>
      </div>

      <p className="mb-2 text-gray-600">
        Map your CSV columns to our system fields. Select which CSV column corresponds to each field.
      </p>

      {/* Progress indicator */}
      {fieldsToMap.length > 0 && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-600">
              Mapping Progress: {Object.keys(columnMappings).filter(k => columnMappings[k]).length} of {fieldsToMap.length} fields
            </span>
            <span className="text-sm text-gray-500">
              {Math.round((Object.keys(columnMappings).filter(k => columnMappings[k]).length / fieldsToMap.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full"
              style={{
                width: `${(Object.keys(columnMappings).filter(k => columnMappings[k]).length / fieldsToMap.length) * 100}%`
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Mapping conflict notification */}
      {Object.keys(mappingConflicts).length > 0 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Mapping Conflicts Detected</h3>
              <div className="mt-1 text-xs text-yellow-700">
                <p>The following CSV columns are mapped to multiple fields:</p>
                <ul className="mt-1 list-disc list-inside">
                  {Object.entries(mappingConflicts).map(([header, fields]) => (
                    <li key={header}>
                      <strong>{header}</strong> is mapped to: {fields.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {fieldsToMap.map((field) => {
          const config = fieldConfigs[field];
          const isRequired = config?.required || false;
          const fieldType = config?.type || 'string';
          const description = config?.description || '';

          // Get suggestions for this field
          const suggestions = suggestedMappings[field] || [];

          // Check if this field is involved in a mapping conflict
          const conflictHeader = columnMappings[field];
          const hasConflict = conflictHeader && mappingConflicts[conflictHeader];

          // Get sample values for the currently selected header
          const sampleValues = getSampleValues(columnMappings[field] || '');

          return (
            <div
              key={field}
              className={`border rounded-lg p-4 bg-white shadow-sm ${
                hasConflict ? 'border-yellow-300' :
                isRequired && !columnMappings[field] ? 'border-red-300' :
                validationErrors[field]?.length ? 'border-red-300' :
                columnMappings[field] ? 'border-green-300' : 'border-gray-300'
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <label className="font-medium text-gray-800">
                  {field.replace(/_/g, ' ')}
                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div className="flex items-center gap-1">
                  {/* Mapping status indicator */}
                  {columnMappings[field] ? (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full flex items-center">
                      <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Mapped
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full">
                      Unmapped
                    </span>
                  )}

                  {/* Field type badge */}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    fieldType === 'currency' ? 'bg-green-100 text-green-800' :
                    fieldType === 'percentage' ? 'bg-blue-100 text-blue-800' :
                    fieldType === 'number' ? 'bg-purple-100 text-purple-800' :
                    fieldType === 'date' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {fieldType}
                  </span>
                </div>
              </div>

              {description && (
                <p className="text-xs text-gray-500 mb-2">{description}</p>
              )}

              <div className="relative">
                <select
                  value={columnMappings[field] || ''}
                  onChange={(e) => onChange(field, e.target.value)}
                  onFocus={() => setShowSuggestionTooltip(field)}
                  onBlur={() => setShowSuggestionTooltip(null)}
                  className={`w-full p-2 border rounded ${
                    hasConflict ? 'border-yellow-300 bg-yellow-50' :
                    isRequired && !columnMappings[field] ? 'border-red-300 bg-red-50' :
                    columnMappings[field] ? 'border-green-300 bg-green-50' : 'border-gray-300'
                  }`}
                >
                  <option value="">-- Select CSV Column --</option>

                  {/* Suggested matches section */}
                  {suggestions.length > 0 && (
                    <optgroup label="Suggested Matches">
                      {suggestions.map((header) => (
                        <option key={`suggested-${header}`} value={header}>
                          {header}
                        </option>
                      ))}
                    </optgroup>
                  )}

                  {/* All headers section */}
                  <optgroup label="All Headers">
                    {csvHeaders
                      .filter(header => !suggestions.includes(header))
                      .map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                  </optgroup>
                </select>

                {/* Tooltip showing suggestion explanation */}
                {showSuggestionTooltip === field && suggestions.length > 0 && (
                  <div className="absolute z-10 left-0 mt-1 p-2 bg-white border rounded shadow-lg text-xs text-gray-600 w-full">
                    <p className="font-medium mb-1">Why these suggestions?</p>
                    <p>Based on field name similarity and data patterns in your CSV.</p>
                  </div>
                )}
              </div>

              {isRequired && !columnMappings[field] && (
                <p className="mt-1 text-xs text-red-500">This field is required</p>
              )}

              {/* Validation errors from backend */}
              {validationErrors[field]?.length > 0 && (
                <div className="mt-1">
                  {validationErrors[field].map((error, idx) => (
                    <p key={idx} className="text-xs text-red-500">{error}</p>
                  ))}
                </div>
              )}

              {/* Conflict warning */}
              {hasConflict && (
                <p className="mt-1 text-xs text-yellow-700">
                  Warning: This column is also mapped to {mappingConflicts[conflictHeader as string]
                    .filter(f => f !== field)
                    .join(', ')}
                </p>
              )}

              {/* Sample data preview */}
              {columnMappings[field] && sampleValues.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500">Sample values:</p>
                  <div className="mt-1 text-xs text-gray-700 flex flex-wrap gap-1">
                    {sampleValues.map((value, idx) => (
                      <span key={idx} className="px-1.5 py-0.5 bg-gray-100 rounded">
                        {value}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Guidance tips */}
      <div className="mt-4 bg-blue-50 p-3 rounded-md text-sm text-blue-700">
        <h3 className="font-medium mb-1">Mapping Tips</h3>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li>Fields marked with an asterisk (*) are required</li>
          <li>The system will attempt to auto-map headers based on name similarity</li>
          <li>If a similar CSV column name is found, it will be pre-selected</li>
          <li>Using consistent naming in your CSV files will improve automatic mapping</li>
        </ul>
      </div>
    </div>
  );
};

export default ColumnMappingInterface;